import { apiClient } from '../api/client'
import { getAppConfig } from '../types/env'
import type { 
  LoginResponse, 
  RefreshResponse, 
  RegisterRequest, 
  User,
  JWTPayload
} from '../types/auth'

class AuthService {
  private tokenKey = 'auth_token'
  private config = getAppConfig()
  
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await apiClient.post('/auth/login', { email, password })
    return response.data
  }
  
  async loginWithToken(token: string): Promise<LoginResponse> {
    const response = await apiClient.post('/auth/login/token', { token })
    return response.data
  }
  
  async getCurrentUser() {
    const response = await apiClient.get('/auth/me')
    return response.data
  }
  
  async logout() {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
    this.clearAuthToken()
  }
  
  async refreshToken(): Promise<RefreshResponse> {
    const response = await apiClient.post('/auth/refresh')
    return response.data
  }
  
  async register(data: RegisterRequest) {
    const response = await apiClient.post('/auth/register', data)
    return response.data
  }
  
  setAuthToken(token: string) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    localStorage.setItem(this.tokenKey, token)
  }
  
  clearAuthToken() {
    delete apiClient.defaults.headers.common['Authorization']
    localStorage.removeItem(this.tokenKey)
  }
  
  getStoredToken(): string | null {
    return localStorage.getItem(this.tokenKey)
  }
  
  // Check for URL token on page load
  checkUrlToken(): string | null {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (token) {
      // Clean URL
      params.delete('token')
      const newUrl = window.location.pathname + 
        (params.toString() ? '?' + params.toString() : '')
      window.history.replaceState({}, '', newUrl)
      return token
    }
    return null
  }
  
  // Initialize auth token from storage on app start
  initializeAuth() {
    const token = this.getStoredToken()
    if (token) {
      this.setAuthToken(token)
    }
  }
  
  // Check if token is likely expired (basic check before API call)
  isTokenLikelyExpired(token: string): boolean {
    try {
      const payload: JWTPayload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000 // Convert to milliseconds
      const refreshThreshold = this.config.auth.tokenRefreshThreshold * 60 * 1000 // Convert minutes to milliseconds
      return Date.now() > exp - refreshThreshold
    } catch {
      return true // If we can't parse it, consider it expired
    }
  }
}

export const authService = new AuthService()